import { isPlatformBrowser } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import type {
  ProfileViewUser,
  ProjectViewUser,
} from '@freelancer/datastore/collections';
import { Location, Review, User } from '@freelancer/datastore/collections';
import { ScreenSizeService } from '@freelancer/screen-size';
import { ButtonColor } from '@freelancer/ui/button';
import { FlagSize } from '@freelancer/ui/flag';
import { trackById } from '@freelancer/ui/helpers';
import {
  IconBackdrop,
  IconBackdropSize,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { TagSize, TagType } from '@freelancer/ui/tag';
import { textVariants } from '@freelancer/ui/text';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import {
  AvatarSize,
  BackgroundColourType,
  type User as UserAvatar,
} from '@freelancer/ui/user-avatar';
import { PriceTierApi } from 'api-typings/projects/projects';
import { Observable, of } from 'rxjs';
import { UsernameSize } from '../username';

enum Perspective {
  REVIEWER = 'REVIEWER',
  REVIEWEE = 'REVIEWEE',
  THIRD_PARTY = 'THIRD_PARTY',
  LOGGED_OUT = 'LOGGED_OUT',
}

@Component({
  standalone: false,
  selector: 'fl-review-card-reply',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewCardReplyComponent {}

@Component({
  standalone: false,
  selector: 'fl-review-card-services',
  template: `
    <div class="Container">
      <fl-picture
        i18n-alt="Service image alt text"
        alt="Service image"
        class="ServiceImg"
        [alignCenter]="true"
        [aspectRatioValue]="'rectangle-medium'"
        [borderRadiusBottom]="true"
        [borderRadiusTop]="true"
        [objectFit]="PictureObjectFit.COVER"
        [src]="imgSrc"
      ></fl-picture>
      <div class="Tag">
        <p
          class="text-color-inherit text-body-20 tablet:text-body-16 font-medium"
          i18n="Service tag"
        >
          {{ serviceTag }}
        </p>
      </div>
      <div class="ServiceLinkContainer">
        <fl-button
          i18n="View service button"
          class="ServiceLink"
          [display]="'flex'"
          [color]="ButtonColor.PRIMARY"
          [link]="serviceLink"
        >
          View service
        </fl-button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./review-card-services.component.scss'],
})
export class ReviewCardServicesComponent {
  ButtonColor = ButtonColor;
  PictureObjectFit = PictureObjectFit;

  @Input() imgSrc: string;
  @Input() serviceTag: string;
  @Input() serviceLink: string;
}

@Component({
  standalone: false,
  selector: 'fl-review-card',
  template: `
    @let isMobileSmall = (isMobileSmall$ | flAsync) ?? false;
    <div
      class="MainContainer"
      flTrackingSection="ReviewItem"
    >
      <div
        class="Container"
        [class.ContainerFix]="reviewCardServicesComponent"
      >
        <div class="Body">
          <div class="Header mb-xxxsmall">
            @if (reviewerAvatar) {
              <fl-link
                [link]="'/u/' + reviewerAvatar.username"
                [underline]="LinkUnderline.NEVER"
              >
                <fl-user-avatar
                  class="mr-xsmall"
                  [backgroundColor]="BackgroundColourType.BLUE_DARK"
                  [size]="AvatarSize.MENU"
                  [sizeTablet]="AvatarSize.MID"
                  [users]="[reviewerAvatar]"
                ></fl-user-avatar>
              </fl-link>
            }
            @if (reviewer) {
              <div class="Header-container ReviewerInfo">
                <div class="ReviewerInfo-username">
                  @if (!reviewer.closed) {
                    <fl-flag
                      class="mr-xxsmall"
                      [countryCode]="reviewerLocation.country?.code"
                      [size]="FlagSize.SMALL"
                      [sizeTablet]="FlagSize.MID"
                    ></fl-flag>
                    <fl-username
                      [displayName]="reviewer.displayName"
                      [showUsernameTag]="false"
                      [size]="UsernameSize.LARGE"
                      [sizeTablet]="UsernameSize.XLARGE"
                      [username]="reviewer.username"
                    ></fl-username>
                  } @else {
                    <p
                      i18n="Former user display name"
                      [class]="reviewClasses"
                      [class.mb-xxxsmall]="true"
                    >
                      Former Client (inactive)
                    </p>
                  }
                </div>
                <fl-rating
                  class="Rating"
                  [control]="reputationControl"
                  [inline]="true"
                  [readOnly]="true"
                  [size]="IconSize.SMALL"
                  [sizeTablet]="IconSize.MID"
                ></fl-rating>
              </div>
            }
            <div class="Header-container ReviewInfo">
              <p class="text-body-16 tablet:text-title-20 font-bold">
                {{
                  hidePrice
                    ? priceTier
                    : (cost
                      | flCurrency
                        : review.currency.code
                        : { withPrecision: false })
                }}
              </p>
              <fl-relative-time
                [date]="review.timeSubmitted"
                [includeSeconds]="false"
                [live]="false"
                [shortFormat]="isMobileSmall"
                [size]="size"
                [sizeDesktop]="sizeDesktop"
                [sizeDesktopXXLarge]="sizeDesktopXXLarge"
                [strict]="true"
              ></fl-relative-time>
            </div>
          </div>

          <p
            [class]="reviewHeadClasses"
            [class.mb-xxxsmall]="true"
          >
            <fl-link
              class="mr-xxxsmall"
              flTrackingLabel="ReviewCardContextLink"
              [color]="LinkColor.INHERIT"
              [link]="review.context.seoUrl"
              [size]="TextSize.INHERIT"
              >{{ review.context.name }}
            </fl-link>
          </p>

          @let hasSummary = summary !== undefined && summary !== '';
          <p
            [class]="reviewClasses"
            [class.mb-xxsmall]="hasSummary"
          >
            @if (hasSummary) {
              {{ summary }}
            } @else {
              {{ review.description }}
            }
          </p>
          @if (hasSummary) {
            <div class="SummaryContainer">
              <p class="SummaryContainer-quote text-title-60">“</p>
              <p
                [class]="reviewClasses"
                [class.italic]="true"
              >
                {{ review.description }}
              </p>
            </div>
          }
          <div class="SkillsAndShareContainer mobile:hide">
            @if (review.skills) {
              <div class="SkillsContainer">
                @let isLoggedIn = isLoggedIn$ | flAsync;
                @for (
                  skill of review.skills | slice: 0 : displayLimit;
                  track trackById($index, skill)
                ) {
                  @if (skill.name !== 'Hire me') {
                    <fl-tag
                      flTrackingLabel="SkillLink"
                      flTrackingReferenceType="skill_id"
                      flTrackingReferenceId="{{ skill.id }}"
                      [link]="
                        isLoggedIn ? '/search/users' : '/hire/' + skill.seoUrl
                      "
                      [queryParams]="
                        isLoggedIn ? { userSkills: skill.id } : undefined
                      "
                      [sizeDesktopXXLarge]="tagSizeDesktopXXLarge"
                      [truncate]="false"
                      [type]="TagType.CLICKABLE"
                    >
                      {{ skill.name }}
                    </fl-tag>
                  }
                }
                @if (review.skills.length > displayLimit) {
                  <p
                    [class]="extraSkillsClasses"
                    i18n="Number of extra skills"
                  >
                    +{{ review.skills.length - displayLimit }} more
                  </p>
                }
              </div>
            }
            @if (!hideShare && !reviewCardServicesComponent) {
              <fl-icon
                class="Share"
                i18n-label="Share review button"
                label="Share"
                [backdrop]="IconBackdrop.TRANSPARENT"
                [backdropSize]="IconbackdropSize.XXXSMALL"
                [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                [clickable]="true"
                [color]="IconColor.FOREGROUND"
                [name]="'ui-share'"
                [size]="IconSize.MID"
                [useIconFont]="true"
                (click)="triggerShare()"
              ></fl-icon>
            }
          </div>
          <div
            class="ActionContainer {{
              reviewCardReplyComponent ? 'mb-small' : 'mb-none'
            }}"
          >
            <div class="ActionContainer-block">
              @if (perspective === Perspective.THIRD_PARTY) {
                @if (!hideHelpful) {
                  <div
                    tabindex="0"
                    class="IconContainer mr-xxsmall"
                    (click)="triggerHelpfulReaction()"
                  >
                    <fl-icon
                      class="mr-xxxsmall"
                      [color]="
                        helpfulReaction
                          ? IconColor.SECONDARY
                          : IconColor.FOREGROUND
                      "
                      [fillIconFont]="helpfulReaction"
                      [name]="'ui-thumb-up'"
                      [size]="IconSize.SMALL"
                      [useIconFont]="true"
                    ></fl-icon>
                    <p
                      i18n="Helpful"
                      [class]="reviewClasses"
                      [class.underline]="true"
                    >
                      Helpful
                    </p>
                  </div>
                }
              }
              @if (perspective === Perspective.REVIEWEE) {
                @if (!hideReply) {
                  <div
                    class="IconContainer mr-xxsmall"
                    tabindex="0"
                    (click)="triggerReply()"
                  >
                    <fl-icon
                      class="mr-xxxsmall"
                      [name]="'ui-comment'"
                      [size]="IconSize.SMALL"
                      [useIconFont]="true"
                    ></fl-icon>
                    <p
                      i18n="Reply"
                      [class]="reviewClasses"
                      [class.underline]="true"
                    >
                      Reply
                    </p>
                  </div>
                }
                @if (!hideFeature && !!reviewee.corporate) {
                  <div
                    class="IconContainer mr-xxsmall"
                    tabindex="0"
                    (click)="triggerFeature()"
                  >
                    <fl-icon
                      class="mr-xxxsmall"
                      [color]="
                        review.featured
                          ? IconColor.SECONDARY
                          : IconColor.FOREGROUND
                      "
                      [fillIconFont]="review.featured"
                      [name]="'ui-star'"
                      [size]="IconSize.SMALL"
                      [useIconFont]="true"
                    ></fl-icon>
                    <p
                      i18n="Toggle feature action cta"
                      [class]="reviewClasses"
                      [class.underline]="true"
                    >
                      {{ review.featured ? 'Remove featured' : 'Feature' }}
                    </p>
                  </div>
                }
              }
              @if (!hideShare) {
                <div
                  class="IconContainer mr-xxsmall"
                  tabindex="0"
                  [class.mobile:show]="!reviewCardServicesComponent"
                  (click)="triggerShare()"
                >
                  <fl-icon
                    class="mr-xxxsmall"
                    [name]="'ui-share'"
                    [size]="IconSize.SMALL"
                    [useIconFont]="true"
                  ></fl-icon>
                  <p
                    i18n="Share"
                    [class]="reviewClasses"
                    [class.underline]="true"
                  >
                    Share
                  </p>
                </div>
              }
              <!-- Review author (the reviewer) can't report their own review -->
              @if (
                !hideReport &&
                (perspective === Perspective.REVIEWEE ||
                  perspective === Perspective.THIRD_PARTY)
              ) {
                <div
                  class="IconContainer"
                  tabindex="0"
                  (click)="triggerReport()"
                >
                  <fl-icon
                    class="mr-xxxsmall"
                    [name]="'ui-flag'"
                    [size]="IconSize.SMALL"
                    [useIconFont]="true"
                  ></fl-icon>
                  <p
                    i18n="Report"
                    [class]="reviewClasses"
                    [class.underline]="true"
                  >
                    Report
                  </p>
                </div>
              }
            </div>
          </div>
          @if (reviewCardReplyComponent) {
            <div class="ReplyContainer">
              <ng-content select="fl-review-card-reply"></ng-content>
            </div>
          }
        </div>
      </div>
      @if (reviewCardServicesComponent) {
        <div class="mobile:hide">
          <ng-content select="fl-review-card-services"></ng-content>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./review-card.component.scss'],
})
export class ReviewCardComponent implements OnInit, OnChanges {
  private screenSize = inject(ScreenSizeService);
  private platformId: object = inject(PLATFORM_ID);
  private auth = inject(Auth);

  readonly isLoggedIn$: Observable<boolean> = this.auth.isLoggedIn();

  AvatarSize = AvatarSize;
  BackgroundColourType = BackgroundColourType;
  IconColor = IconColor;
  IconbackdropSize = IconBackdropSize;
  IconBackdrop = IconBackdrop;
  IconBackdropStyle = IconBackdropStyle;
  IconSize = IconSize;
  LinkColor = LinkColor;
  Perspective = Perspective;
  TagType = TagType;
  LinkUnderline = LinkUnderline;
  UsernameSize = UsernameSize;
  FlagSize = FlagSize;
  TextSize = TextSize;
  trackById = trackById;

  reviewerAvatar: UserAvatar;
  reputationControl = new FormControl(0, { nonNullable: true });
  readonly displayLimit = 5;
  helpfulReaction = false;

  cost: number;
  hidePrice = false;
  perspective: Perspective;

  @Input() hideHelpful = false;
  @Input() hideReply = false;
  @Input() hideReport = false;
  @Input() hideFeature = false;
  @Input() hideShare = false;
  @Input() loggedInUser: User;
  @Input() reviewee: User | ProfileViewUser;
  @Input() reviewer: User | ProjectViewUser;
  @Input() reviewerLocation: Location;
  @Input() review: Review;
  /** A brief summary of the review context (e.g. summary of project description) */
  @Input() summary?: string;
  @Input() size = TextSize.BODY_16;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;
  @Input() tagSizeDesktopXXLarge: TagSize = TagSize.MID;

  @Output() clickHelpful = new EventEmitter<void>();
  @Output() clickReply = new EventEmitter<void>();
  @Output() clickReport = new EventEmitter<void>();
  @Output() clickShare = new EventEmitter<void>();
  @Output() clickFeature = new EventEmitter<void>();

  @ContentChild(ReviewCardReplyComponent)
  reviewCardReplyComponent: ReviewCardReplyComponent;

  @ContentChild(ReviewCardServicesComponent)
  reviewCardServicesComponent: ReviewCardServicesComponent;

  reviewHeadClasses: string;
  reviewClasses: string;
  extraSkillsClasses: string;

  isMobileSmall$: Observable<boolean>;

  get priceTier(): string | undefined {
    switch (this.review.priceTier) {
      case PriceTierApi.LOW_PRICE:
        return '$';
      case PriceTierApi.MID_PRICE:
        return '$$';
      case PriceTierApi.HIGH_PRICE:
        return '$$$';
      case PriceTierApi.ULTRA_HIGH_PRICE:
        return '$$$$';
      default:
        return undefined;
    }
  }

  ngOnInit(): void {
    this.computeClasses();

    this.isMobileSmall$ = this.isMobileSmallViewport();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.reviewer && 'reviewer' in changes) {
      this.reviewerAvatar = {
        avatar: this.reviewer.avatar,
        username: this.reviewer.username,
      };
    }

    if (
      'reviewer' in changes ||
      'reviewee' in changes ||
      'loggedInUser' in changes
    ) {
      if (
        this.loggedInUser &&
        this.reviewer &&
        this.loggedInUser.id === this.reviewer.id
      ) {
        this.perspective = Perspective.REVIEWER;
      } else if (
        this.loggedInUser &&
        this.reviewee &&
        this.loggedInUser.id === this.reviewee.id
      ) {
        this.perspective = Perspective.REVIEWEE;
      } else if (this.loggedInUser) {
        this.perspective = Perspective.THIRD_PARTY;
      } else {
        this.perspective = Perspective.LOGGED_OUT;
      }
    }

    if (this.review && 'review' in changes) {
      this.reputationControl = new FormControl(this.review.rating, {
        nonNullable: true,
      });

      // If the review is sealed and the logged in user is not the reviewer or reviewee, show price undisclosed
      if (
        this.review.sealed &&
        (!this.loggedInUser ||
          (this.review.fromUserId !== this.loggedInUser.id &&
            this.review.toUserId !== this.loggedInUser.id))
      ) {
        this.hidePrice = true;
      } else {
        this.cost = this.review.paidAmount || this.review.bidAmount;
      }
    }

    if (
      'size' in changes ||
      'sizeDesktop' in changes ||
      'sizeDesktopXXLarge' in changes
    ) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.reviewHeadClasses = textVariants({
      size: this.size,
      sizeDesktop: this.sizeDesktop,
      sizeDesktopXXL: this.sizeDesktopXXLarge,
      weight: FontWeight.MEDIUM,
    });

    this.reviewClasses = textVariants({
      size: this.size,
      sizeDesktop: this.sizeDesktop,
      sizeDesktopXXL: this.sizeDesktopXXLarge,
    });

    this.extraSkillsClasses = textVariants({
      class: 'ExtraSkills',
      size: this.size,
    });
  }

  triggerHelpfulReaction(): void {
    // Toggle button
    this.helpfulReaction = !this.helpfulReaction;

    this.clickHelpful.emit();
  }

  triggerReply(): void {
    this.clickReply.emit();
  }

  triggerReport(): void {
    this.clickReport.emit();
  }

  triggerShare(): void {
    this.clickShare.emit();
  }

  triggerFeature(): void {
    this.clickFeature.emit();
  }

  private isMobileSmallViewport(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(false);
    }

    return this.screenSize.isMobileSmall();
  }
}
